import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Loan, LoanEvent } from '@/types';
import { listEvents } from '@/lib/db';
import { summarizeLoanState, describeEstimateBasis, getLoanStatusLabel, REGULAR_EMI_BASIS_LABEL } from '@/lib/calc/loanState';
import { todayIso, isBefore } from '@/lib/calc/dates';
import { computeSavingsBaselines, computeSavingsSummary } from '@/lib/calc/savingsBaselines';
import { LoanStatusBadge } from '@/components/StatusBadge';
import { formatInr, formatDate } from '@/lib/format';
import { EVENT_TYPE_ICONS, summarizeEvent, eventDateLabel } from '@/features/events/eventDisplay';
import { EVENT_TYPE_LABELS } from '@/types';
import { ChevronIcon, HomeIcon } from '@/components/icons';

interface HomePageProps {
  loan: Loan;
}

/**
 * Home — the primary everyday view of the loan. Per the approved
 * redesign, this is a PURE PRESENTATION layer over the SAME calculation
 * engine and savings-baseline functions already used by Schedule — no
 * new calculation logic lives here, and Schedule itself is untouched by
 * this change.
 *
 * SECTIONS (approved order):
 *   1. Header (lender, status)
 *   2. As-of date selector (own independent state, mirrors Schedule's
 *      own selector but is NOT shared/synced with it)
 *   3. Current Position
 *   4. Loan Progress (with a simple repaid-vs-remaining bar)
 *   5. Financial Impact (savings baselines)
 *   6. Projection
 *   7. Recent Events (latest 3-4, reusing Timeline's own display helpers)
 *   8. Base Loan Terms (collapsed by default)
 */
export function HomePage({ loan }: HomePageProps) {
  const [events, setEvents] = useState<LoanEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [baseTermsExpanded, setBaseTermsExpanded] = useState(false);

  // Home's OWN "As of" date state — independent of Schedule's, per the
  // approved scope (no shared/synced state between the two screens).
  const [asOfDate, setAsOfDate] = useState<string>(() => todayIso());

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listEvents({ loanId: loan.id }).then((result) => {
      if (!cancelled) {
        setEvents(result);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loan.id]);

  // Mirrors Schedule's own walk-start logic (earliest of loanStartDate
  // and first disbursement) so the date picker's minimum bound is
  // consistent with what the engine itself treats as "before this loan
  // existed" — same computation, independently maintained per screen
  // exactly as Schedule's own copy is, since both are small, self-
  // contained presentation-layer bounds checks, not shared calculation
  // logic.
  const earliestValidDate = useMemo(() => {
    const firstDisbursementDate = events
      .filter((e) => e.type === 'disbursement' && !e.supersededAt)
      .map((e) => e.effectiveDate)
      .sort()[0];
    if (loan.loanStartDate && firstDisbursementDate) {
      return isBefore(firstDisbursementDate, loan.loanStartDate) ? firstDisbursementDate : loan.loanStartDate;
    }
    return loan.loanStartDate ?? firstDisbursementDate;
  }, [loan.loanStartDate, events]);

  const today = todayIso();
  const isFutureDate = asOfDate > today;

  // One shared, engine-backed view (position as of the selected date + the full projection).
  const loanState = useMemo(() => {
    if (isLoading) return undefined;
    return summarizeLoanState({ loan, events, asOfDate });
  }, [loan, events, isLoading, asOfDate]);

  const currentPosition = loanState?.position;
  const currentProjection = loanState?.projection;
  const statusInfo = getLoanStatusLabel(loan, events);

  const savings = useMemo(() => {
    if (isLoading) return undefined;
    const baselines = computeSavingsBaselines(loan, events);
    return computeSavingsSummary(baselines);
  }, [loan, events, isLoading]);

  const totalDisbursed = useMemo(
    () =>
      events
        .filter((e): e is Extract<LoanEvent, { type: 'disbursement' }> => e.type === 'disbursement' && !e.supersededAt)
        .reduce((sum, e) => sum + e.amount, 0),
    [events]
  );

  const recentEvents = useMemo(() => events.slice(0, 4), [events]);

  function handleAsOfDateChange(next: string) {
    if (!next) return;
    if (earliestValidDate && next < earliestValidDate) {
      // Never allow selecting a date before the loan timeline starts.
      return;
    }
    setAsOfDate(next);
  }

  if (isLoading) {
    return (
      <div className="stack">
        <h1>Home</h1>
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  const hasSchedule = !!loanState && !!currentPosition && !!currentProjection && !!savings && currentProjection.months.length > 0;

  return (
    <div className="stack">
      <div>
        <h1>Home</h1>
        <p>{loan.lender}</p>
        <LoanStatusBadge status={loan.status} label={statusInfo.label} isDraft={statusInfo.isDraft} />
        {statusInfo.isDraft && (
          <p style={{ marginTop: 8 }}>
            These figures are your best current expectation, not yet confirmed by an LIC sanction letter or KFS.
          </p>
        )}
      </div>

      {!hasSchedule && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <HomeIcon size={28} />
          </div>
          <p>
            No schedule yet — record at least one disbursement on the Timeline (and a Loan Start Date on Settings) to
            see your loan position here.
          </p>
        </div>
      )}

      {hasSchedule && loanState && currentPosition && currentProjection && savings && (
        <>
          <div className="card-supporting">
            <div className="field-group" style={{ marginBottom: 0 }}>
              <label htmlFor="homeAsOfDate">As of</label>
              <input
                id="homeAsOfDate"
                type="date"
                value={asOfDate}
                min={earliestValidDate}
                onChange={(e) => handleAsOfDateChange(e.target.value)}
              />
              {isFutureDate && (
                <span className="field-hint">
                  You've selected a future date. The figures below are a PROJECTION for that date, not your confirmed
                  current state.
                </span>
              )}
            </div>
          </div>

          <div className="card-hero">
            <div className="card-title">{isFutureDate ? 'Projected Position' : 'Current Position'}</div>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span className="text-muted">{isFutureDate ? 'Projected Outstanding Principal' : 'Outstanding Principal'}</span>
              <span className="mono figure-hero">
                {formatInr(isFutureDate ? currentPosition.summary.currentOutstandingPrincipal : loanState.recordedPrincipal, true)}
              </span>
            </div>
            <div className="stack" style={{ gap: 10, marginTop: 10 }}>
              <SummaryRow
                label={isFutureDate ? 'Projected Interest Rate' : 'Current Interest Rate'}
                value={`${currentPosition.summary.currentApplicableAnnualRatePercent.toFixed(2)}%`}
              />
              <SummaryRow
                label={isFutureDate ? 'Projected EMI' : 'Current EMI'}
                value={currentPosition.summary.isLoanClosed ? '—' : formatInr(currentPosition.summary.currentApplicableEmi, true)}
                badge={
                  loanState.regularEmi && loanState.regularEmi.basis === 'derived_over_contract_tenure' ? REGULAR_EMI_BASIS_LABEL.derived_over_contract_tenure : undefined
                }
              />
              {loanState.nextPayment && !currentPosition.summary.isLoanClosed && (
                <SummaryRow
                  label={`Next Payment · ${formatDate(loanState.nextPayment.emiDate)}`}
                  value={formatInr(loanState.nextPayment.emiAmount, true)}
                  badge="Est."
                />
              )}
              {loanState.lenderReported?.instalmentAmount !== undefined && (
                <SummaryRow
                  label="LIC Reported Instalment"
                  value={formatInr(loanState.lenderReported.instalmentAmount, true)}
                  badge="LIC"
                />
              )}
              <SummaryRow
                label="Remaining Tenure"
                value={currentPosition.summary.isLoanClosed ? '0 months' : `${loanState.remainingRegularInstalments ?? 0} months`}
              />
              {loanState.contractualMaturity && (
                <SummaryRow
                  label="Contractual Maturity"
                  value={formatDate(loanState.contractualMaturity.date)}
                  badge={loanState.contractualMaturity.source === 'lender_reported' ? 'LIC' : undefined}
                />
              )}
              {loanState.modelledBalanceDiffersFromRecorded && !isFutureDate && (
                <SummaryRow
                  label="Est. Balance After Scheduled EMIs"
                  value={formatInr(currentPosition.summary.currentOutstandingPrincipal, true)}
                  badge="Est."
                />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Loan Progress</div>
            <ProgressBar
              repaid={currentPosition.summary.totalPrincipalRepaid}
              total={totalDisbursed || loan.sanctionAmount}
            />
            <div className="stack" style={{ gap: 10, marginTop: 14 }}>
              <SummaryRow label="Total Disbursed" value={`${formatInr(totalDisbursed, true)} of ${formatInr(loan.sanctionAmount, true)} sanctioned`} />
              {loanState.undisbursedAmount > 0 && <SummaryRow label="Undisbursed" value={formatInr(loanState.undisbursedAmount, true)} />}
              <SummaryRow
                label={isFutureDate ? 'Principal Repaid (Projected)' : 'Principal Repaid So Far'}
                value={formatInr(currentPosition.summary.totalPrincipalRepaid, true)}
              />
              {currentPosition.summary.totalPrepayments > 0 && (
                <SummaryRow
                  label={isFutureDate ? 'Prepayments (Projected)' : 'Prepayments Made So Far'}
                  value={formatInr(currentPosition.summary.totalPrepayments, true)}
                />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Financial Impact</div>
            <div className="stack" style={{ gap: 10 }}>
              <SummaryRow
                label="Projected Total Interest"
                value={formatInr(currentProjection.summary.projectedTotalInterest ?? currentProjection.summary.totalInterestPaid, true)}
                badge="Est."
              />
              <SummaryRow
                label="Projected Total Payment"
                value={formatInr(currentProjection.summary.totalEmiPaid + currentProjection.summary.totalPrepayments, true)}
              />
              <SummaryRow label="Interest Saved vs. Original" value={formatInr(savings.interestSavedVsOriginal, true)} />
              <SummaryRow label="Total Payment Saved vs. Original" value={formatInr(savings.totalPaymentSavedVsOriginal, true)} />
              <SummaryRow label="Time Saved vs. Original" value={`${savings.timeSavedVsOriginalMonths} months`} />
            </div>
            <p className="field-hint" style={{ marginTop: 12, marginBottom: 0 }}>
              Of this, the effect of rate movements alone (independent of your prepayments/repayment decisions) is{' '}
              {formatInr(savings.interestSavedDueToRateMovementAlone, true)} in interest —{' '}
              {savings.interestSavedDueToRateMovementAlone >= 0 ? 'a saving' : 'an added cost'} from how the rate has
              moved.
            </p>
          </div>

          {!currentPosition.summary.isLoanClosed && (
            <div className="card">
              <div className="card-title">Projection</div>
              <div className="stack" style={{ gap: 10 }}>
                {currentProjection.summary.projectedClosureDate && (
                  <SummaryRow label="Projected Closure Date" value={formatDate(currentProjection.summary.projectedClosureDate)} badge="Est." />
                )}
              </div>
              {describeEstimateBasis(loanState.projectionInfo) && (
                <p className="field-hint" style={{ marginTop: 10, marginBottom: 0 }}>
                  {describeEstimateBasis(loanState.projectionInfo)}
                </p>
              )}
            </div>
          )}

          {currentPosition.summary.isLoanClosed && currentPosition.summary.projectedClosureDate && (
            <div className="card">
              <div className="card-title">Projection</div>
              <SummaryRow
                label={isFutureDate ? 'Projected to Close On' : 'Closed On'}
                value={formatDate(currentPosition.summary.projectedClosureDate)}
              />
            </div>
          )}

          {recentEvents.length > 0 && (
            <div className="card">
              <div className="card-title">Recent Events</div>
              <div className="timeline-list">
                {recentEvents.map((event) => (
                  <Link
                    key={event.id}
                    to="/timeline"
                    className="timeline-item"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="timeline-icon">{EVENT_TYPE_ICONS[event.type]}</div>
                    <div className="timeline-item-body">
                      <div className="timeline-item-title">{summarizeEvent(event)}</div>
                      <div className="timeline-item-meta">
                        {EVENT_TYPE_LABELS[event.type]} · {eventDateLabel(event)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="card-supporting">
        <button
          type="button"
          onClick={() => setBaseTermsExpanded((e) => !e)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            color: 'var(--color-text)',
          }}
        >
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="card-title" style={{ margin: 0 }}>
              Base Loan Terms
            </div>
            <ChevronIcon size={18} className={`disclosure-chevron${baseTermsExpanded ? ' is-expanded' : ''}`} />
          </div>
        </button>

        {baseTermsExpanded && (
          <div className="stack" style={{ gap: 12, marginTop: 14 }}>
            <SummaryRow label="Sanction Amount" value={formatInr(loan.sanctionAmount)} />
            <SummaryRow label="Approved Amount" value={formatInr(loan.approvedAmount)} />
            <SummaryRow label="Original / Expected Rate" value={`${loan.originalAnnualRatePercent.toFixed(2)}% p.a.`} />
            <SummaryRow label="Original Tenure" value={`${loan.originalTenureMonths} months`} />
            <SummaryRow
              label="Original EMI (Full Sanctioned Amount)"
              value={formatInr(loan.originalEmiAtFullDisbursement, true)}
              badge={loan.isEmiManuallyOverridden ? 'Manually Entered' : 'Calculated'}
            />
            <SummaryRow label="EMI Day of Month" value={String(loan.emiDayOfMonth)} />
            <SummaryRow label="Loan Start Date" value={formatDate(loan.loanStartDate)} />
            <SummaryRow
              label="Regular EMI Start Date"
              value={formatDate(loan.regularEmiStartDate ?? loanState?.projection.summary.arrangement.startDate)}
              badge={loan.regularEmiStartDate ? undefined : 'Est.'}
            />
            {loan.regularEmiAmount !== undefined && (
              <SummaryRow label="Regular EMI (entered)" value={formatInr(loan.regularEmiAmount, true)} />
            )}
            {loan.loanAccountNumber && <SummaryRow label="Loan Account Number" value={loan.loanAccountNumber} />}
            <Link to="/settings" className="btn btn-block" style={{ textDecoration: 'none', marginTop: 8 }}>
              Edit Loan Details
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, badge, emphasize }: { label: string; value: string; badge?: string; emphasize?: boolean }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <span className="text-muted">{label}</span>
      <span className={`mono${emphasize ? ' figure-secondary' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {badge && (
          <span className={`badge ${badge === 'Calculated' || badge === 'Est.' ? 'badge-projected' : 'badge-actual_lic'}`}>{badge}</span>
        )}
        {value}
      </span>
    </div>
  );
}

/**
 * Simple, mobile-friendly repaid-vs-remaining bar. Pure presentation
 * over already-computed figures — no calculation of its own beyond a
 * percentage for the bar width.
 */
function ProgressBar({ repaid, total }: { repaid: number; total: number }) {
  const percent = total > 0 ? Math.min(100, Math.max(0, (repaid / total) * 100)) : 0;
  return (
    <div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: 'var(--color-surface-raised)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            background: 'var(--color-accent-strong)',
            borderRadius: 4,
          }}
        />
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
        <span className="field-hint">{percent.toFixed(0)}% repaid</span>
        <span className="field-hint">{formatInr(Math.max(0, total - repaid), true)} remaining</span>
      </div>
    </div>
  );
}
